/**
 * Every gameplay constant lives here.
 *
 * Horror is a pacing problem, not a code problem — expect to twist these knobs
 * far more often than you touch the systems that read them. Nothing outside
 * this file should hardcode a speed, radius, duration, or threshold.
 */

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------

export const TICK_RATE = 20; // server simulation ticks per second
export const TICK_MS = 1000 / TICK_RATE;
export const CLIENT_SEND_RATE = 20; // position reports per second
export const INTERP_DELAY_MS = 100; // remote player interpolation buffer

export const MAX_PLAYERS = 6;

// ---------------------------------------------------------------------------
// World
// ---------------------------------------------------------------------------

export const WORLD = {
  /** Playable area is a square centred on the origin: [-SIZE/2, +SIZE/2]. */
  SIZE: 512,
  /** Beyond this radius the forest becomes an impassable wall. */
  PLAYABLE_RADIUS: 236,
  /** Terrain vertical amplitude in metres. */
  HEIGHT_SCALE: 14,
  /** Base frequency of the terrain noise. Lower = broader, gentler hills. */
  TERRAIN_FREQ: 0.0022,
  TERRAIN_OCTAVES: 4,
  /** Mesh resolution: vertices per side. */
  TERRAIN_SEGMENTS: 192,
} as const;

export const TREES = {
  /** Candidate slots on the jittered generation grid. */
  GRID_SPACING: 4.2,
  /** Jitter applied within each grid cell, as a fraction of spacing. */
  JITTER: 0.85,
  /** Noise-driven density: cells below this threshold stay empty (clearings). */
  DENSITY_FREQ: 0.006,
  DENSITY_THRESHOLD: -0.28,
  /** Trees are culled on slopes steeper than this (radians from vertical). */
  MAX_SLOPE: 0.62,
  MIN_HEIGHT: 7,
  MAX_HEIGHT: 19,
  MIN_RADIUS: 0.22,
  MAX_RADIUS: 0.48,
  VARIANTS: 3,
  /** Trees are cleared this far around towers and landmarks. */
  CLEARING_RADIUS: 11,
} as const;

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export const PLAYER = {
  RADIUS: 0.35,
  HEIGHT: 1.8,
  EYE_HEIGHT: 1.68,
  CROUCH_EYE_HEIGHT: 1.02,

  WALK_SPEED: 3.1,
  SPRINT_SPEED: 6.0,
  CROUCH_SPEED: 1.35,
  CLIMB_SPEED: 2.2,

  ACCEL: 42,
  AIR_ACCEL: 6,
  FRICTION: 11,
  GRAVITY: 22,
  /**
   * Steps up to this height are walked over.
   *
   * This is what replaces a jump, and why removing the jump cost nothing: the
   * only vertical obstacles in the forest are small rises, which you walk up,
   * and towers, which you climb by ladder.
   */
  STEP_HEIGHT: 0.55,
  /** Above this slope you slide instead of walking. */
  MAX_WALK_SLOPE: 0.86,

  STAMINA_MAX: 100,
  STAMINA_DRAIN: 17, // per second while sprinting
  STAMINA_REGEN: 13, // per second while recovering
  STAMINA_REGEN_DELAY: 1.4, // seconds after sprinting before regen starts
  /**
   * Exhaustion lock.
   *
   * You may sprint whenever you have stamina — but if you run yourself all the
   * way to zero you must recover to this level before sprinting again. That is
   * the only situation where having some stamina is not enough, and it exists
   * so that emptying the bar is a real mistake rather than a free stutter.
   */
  STAMINA_EXHAUSTED_RECOVERY: 30,
  /** Minimum stamina to begin a sprint under normal conditions. */
  STAMINA_SPRINT_FLOOR: 1,

  HEALTH_MAX: 100,

  /** Seconds of the grab/thrash sequence before the screen is fully black. */
  DEATH_SEQUENCE: 4.2,
  /** Interaction reach for towers, items, hiding spots. */
  INTERACT_RANGE: 2.6,
} as const;

// ---------------------------------------------------------------------------
// Noise emission — the core perception currency
// ---------------------------------------------------------------------------

/**
 * Continuous noise emitted by player state, in arbitrary units where 1.0 is a
 * full sprint. The monster's hearing check is `noise * falloff(distance)`.
 */
export const NOISE = {
  IDLE: 0.02,
  CROUCH: 0.09,
  WALK: 0.34,
  SPRINT: 1.0,
  CLIMBING: 0.22,
  /** Hiding suppresses noise almost entirely — but see BREATH below. */
  HIDDEN: 0.015,
  /**
   * A radio left switched on.
   *
   * Slightly under a walking pace, so it never dominates while you are moving
   * normally — but it swamps a crouch and ruins a hiding place, which is what
   * makes the switch a decision rather than a formality. Scan and be heard, or
   * go quiet and navigate blind.
   */
  RADIO: 0.3,
  /** Gasping after a failed breath-hold. A very bad moment to be found. */
  GASP: 0.85,

  /** One-shot event noises, emitted at a world position. */
  EVENT: {
    FLASHLIGHT_TOGGLE: 0.15,
    ITEM_PICKUP: 0.12,
    FLARE_IGNITE: 1.6,
    /** Transmitting is a deliberate, enormous beacon. That is the point. */
    TRANSMIT: 2.4,
    LANDING: 0.45,
    TREE_BRUSH: 0.2,
  },

  /** Distance in metres at which a 1.0 noise falls below the hearing floor. */
  FALLOFF_RANGE: 95,
  /** Hearing check floor. Lower = the monster hears more. */
  HEARING_FLOOR: 0.06,
} as const;

// ---------------------------------------------------------------------------
// Monster
// ---------------------------------------------------------------------------

export const MONSTER = {
  HEIGHT: 12.5,
  /** Collision/grab radius at the base. */
  RADIUS: 1.6,

  ROAM_SPEED: 3.4,
  INVESTIGATE_SPEED: 4.6,
  HUNT_SPEED: 5.8,
  CHASE_SPEED: 8.2,
  TURN_RATE: 2.2, // radians/second

  /** Vision */
  VIEW_CONE: 1.22, // radians, half-angle
  VIEW_RANGE: 52,

  /**
   * He does not look up.
   *
   * Perception was entirely two-dimensional, so someone thirty metres up a
   * radio mast was treated exactly like someone standing in front of him. That
   * made the towers indefensible: no way to wait out his presence before
   * transmitting, and no way to be safe after.
   *
   * Above SIGHT_HEIGHT_BLIND — a little over his own head height — he simply
   * cannot see you. It is also what the maintenance note taped to Tower 1
   * tells you to do.
   */
  SIGHT_HEIGHT_FULL: 4,
  SIGHT_HEIGHT_BLIND: 14,
  /**
   * How high he can reach. He is twelve metres tall, so the bottom of a ladder
   * is not safe — but the top of one is.
   */
  GRAB_HEIGHT: 12,
  /** A flashlight aimed at the monster multiplies your visual signature. */
  FLASHLIGHT_VISIBILITY_MULT: 3.4,
  /** Crouching reduces your visual signature. */
  CROUCH_VISIBILITY_MULT: 0.45,

  /** Alertness (0-100) gates every state transition. */
  ALERT_MAX: 100,
  ALERT_DECAY: 2.8, // per second
  ALERT_INVESTIGATE: 25,
  ALERT_HUNT: 55,
  ALERT_CHASE: 82,
  /** Gain per second of sustained detection at full strength. */
  ALERT_GAIN_HEARD: 34,
  ALERT_GAIN_SEEN: 62,

  /** Seconds of no contact before the monster gives up a chase. */
  CHASE_LOSE_TIME: 7.0,
  /** How long it searches the last known position before roaming again. */
  SEARCH_DURATION: 16,
  /** Radius it wanders around the last known position while searching. */
  SEARCH_RADIUS: 18,

  /**
   * Distraction — what a thrown flare actually does.
   *
   * Competing on raw alertness is not enough: a player in the open generates
   * up to ALERT_GAIN_SEEN per second, far above anything a noise source can
   * produce, so a flare thrown while you were visible did nothing at all. And
   * once he had committed to a chase there was no path out of it.
   *
   * A flare now interrupts him outright — he breaks off, goes to look at it,
   * and while he is doing so his interest in players is damped. It buys a
   * window, not immunity: he can still catch you if you run into him.
   */
  DISTRACT_DURATION: 14,
  /** Player detection multiplier while distracted. */
  DISTRACT_DAMPEN: 0.3,
  /** He mills around the distraction rather than standing on it. */
  DISTRACT_RADIUS: 7,

  GRAB_RADIUS: 3.2,
  /** He is enormous: trees below this radius are smashed through, not avoided. */
  SMASH_TREE_RADIUS: 0.42,
  /** Cooldown between tree-crash sound events, to avoid audio spam. */
  SMASH_COOLDOWN: 0.55,

  /** Path recompute budget. */
  REPATH_INTERVAL: 0.6, // seconds
  /** Spawn at least this far from any player. */
  MIN_SPAWN_DISTANCE: 120,

  /**
   * Repulsion — what a completed transmission actually does to him.
   *
   * The array does not call for help so much as drive him off; the towers were
   * built to keep him away from the road, and the last log entry says so. This
   * is also what makes the central risk a gamble rather than a trap: without
   * it, finishing fourteen seconds of maximum noise at the top of a ladder
   * left you grabbed on the platform with no way down.
   */
  REPEL_DURATION: 22, // seconds he flees after a tower activates
  REPEL_SPEED: 6.6,
  /** He stops fleeing once this far from the tower that drove him off. */
  REPEL_DISTANCE: 165,
} as const;

// ---------------------------------------------------------------------------
// Confrontation — the forced look
// ---------------------------------------------------------------------------

/**
 * When the monster commits to a chase and closes inside RADIUS, the target is
 * briefly frozen and their head is turned to look up at him.
 *
 * The point is a guarantee: dying to something you never saw is not a scare,
 * it is a bug report. Because a grab can only happen in the Chase state, and
 * every chase must cross this radius on its way from far to grabbing distance,
 * a player provably cannot be killed without being shown the thing first.
 *
 * The fairness cost is real and deliberately bounded. At CHASE_SPEED he closes
 * about ten metres during the freeze, so RADIUS is set well outside grabbing
 * range to leave a genuine window afterwards. It fires at most once per chase
 * so a long pursuit is not a slideshow, and COOLDOWN stops a monster that
 * repeatedly reacquires you from stun-locking you to death.
 */
export const CONFRONT = {
  /** He must be at least this close, and committed to a chase, to trigger. */
  RADIUS: 26,
  /** Total seconds of lost control. */
  DURATION: 1.25,
  /** Seconds for the head to finish snapping around. */
  TURN_TIME: 0.32,
  /** Minimum seconds between confrontations, across all chases. */
  COOLDOWN: 60,
  /** Look at this fraction of his height — you should be craning upward. */
  AIM_HEIGHT: 0.78,
  /** Degrees of FOV pull-in, which reads as involuntary focus. */
  FOV_PUNCH: 9,
} as const;

// ---------------------------------------------------------------------------
// Director — global pacing above the monster FSM
// ---------------------------------------------------------------------------

export const DIRECTOR = {
  /** Grace period at match start before the monster can escalate. */
  OPENING_GRACE: 30, // seconds
  /** Grace after any player dies, so a wipe doesn't cascade. */
  DEATH_GRACE: 12,
  /** If nothing scary has happened for this long, manufacture something. */
  QUIET_THRESHOLD: 22,
  /** Ambient cue cadence (seconds), randomised within these bounds. */
  AMBIENT_MIN: 10,
  AMBIENT_MAX: 26,

  /**
   * The prowl leash.
   *
   * Roaming picks a target relative to his own position, so left alone he
   * random-walks and can settle in a corner of a 472m map. His hearing only
   * carries 95m, so once he drifts out of earshot nothing pulls him back and
   * he simply never appears — several matches can pass without an encounter.
   *
   * When he has been out of everyone's world for too long the Director steers
   * his next roam toward a player's general area. Not at them: to a point some
   * distance off, so he arrives wandering rather than homing, and the player
   * still has to give themselves away.
   */
  PRESENCE_RADIUS: 115,
  PROWL_THRESHOLD: 24, // seconds out of everyone's world before nudging
  PROWL_NEAR: 45,
  PROWL_FAR: 85,
  /** Distance range for ambient stomp/siren cues relative to the players. */
  AMBIENT_NEAR: 55,
  AMBIENT_FAR: 170,
  /**
   * Alertness multiplier by player count. Solo still gets an easier ride, but
   * not so much easier that he never notices you at all.
   */
  PLAYER_COUNT_SCALING: [1.0, 0.92, 0.98, 1.04, 1.1, 1.16, 1.22],
  /** Once rescue is inbound the monster stops being subtle. */
  ENDGAME_ALERT_FLOOR: 60,
} as const;

// ---------------------------------------------------------------------------
// Radio towers
// ---------------------------------------------------------------------------

export const TOWERS = {
  COUNT: 5,
  /** Towers needed to call in rescue. */
  REQUIRED: 3,
  HEIGHT: 34,
  /** Minimum separation between towers, metres. */
  MIN_SEPARATION: 95,
  BASE_RADIUS: 4.2,
  /** Ladder climb volume half-extent. */
  LADDER_WIDTH: 1.1,

  /**
   * Radius of the safe zone an activated tower projects, permanently.
   *
   * He physically cannot enter one. This is the strategic payoff for the
   * game's riskiest action: each tower you light turns a patch of forest into
   * ground you can cross without listening. It also gives the map a shape —
   * islands of safety with dark water between them — and gives a chased player
   * somewhere specific to run to.
   */
  SAFE_RADIUS: 42,

  /**
   * Transmit minigame.
   *
   * The numbers here are load-bearing and were badly wrong at first: filling
   * took 1/14 ≈ 0.071 per second while being off-signal drained 0.45 per
   * second, so the bar lost ground six times faster than it gained it and
   * could not be completed without near-perfect tracking. Decay must stay
   * modest relative to the fill rate — the minigame is meant to be tense, not
   * unwinnable.
   */
  TRANSMIT_DURATION: 14, // seconds of held signal required
  /**
   * The signal is a bounded oscillation around the middle of the dial rather
   * than an accumulating drift. The earlier version integrated a sine, which
   * let the target wander to an extreme and pin against the end of the track.
   */
  DRIFT_AMPLITUDE: 0.32,
  DRIFT_FREQ: 0.55, // radians per second
  /** Tolerance band, in normalised dial units, that counts as "on signal". */
  TOLERANCE: 0.13,
  /** Progress lost per second while off-signal. */
  DECAY_RATE: 0.12,
  /** How fast the dial moves under A/D, in dial units per second. */
  DIAL_SPEED: 0.9,
  /** Signal booster item shortens the hold. */
  BOOSTER_MULT: 0.6,
} as const;

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export const ITEMS = {
  /** Flashlight battery, in seconds of continuous use. */
  BATTERY_MAX: 240,
  BATTERY_REFILL: 150,
  FLARE_BURN_TIME: 26,
  /** How strongly a burning flare pulls the monster, as a noise value. */
  FLARE_LURE: 1.1,
  /** Spawn counts across the whole map. */
  SPAWN_COUNTS: {
    battery: 14,
    flare: 8,
    map: 3,
    compass: 3,
    radio: 3,
    booster: 2,
  },
} as const;

// ---------------------------------------------------------------------------
// Carried gear — the passive items that work while held
// ---------------------------------------------------------------------------

/**
 * Map, compass and radio are informational rather than consumable. They take
 * effect while selected in hand, which makes carrying them a real choice: you
 * cannot be reading the map and holding a flare ready at the same time.
 */
export const GEAR = {
  /** Get this close to a tower and the map remembers it. */
  MAP_DISCOVER_RADIUS: 48,
  /** Beyond this the radio hears nothing. */
  RADIO_RANGE: 135,
  /** Static burst spacing at full signal and at the edge of range. */
  RADIO_MIN_INTERVAL: 0.22,
  RADIO_MAX_INTERVAL: 1.9,
  /** Signal strength above which the carrier tone starts breaking through. */
  RADIO_CARRIER_THRESHOLD: 0.72,
} as const;

// ---------------------------------------------------------------------------
// Hiding
// ---------------------------------------------------------------------------

export const HIDING = {
  COUNT: 26,

  /**
   * How many bodies fit, by kind.
   *
   * Sized to the geometry each kind actually renders as (see buildHidingSpot),
   * because a capacity the player cannot see is a capacity they cannot plan
   * around: a four-metre hollow log obviously takes two, a locker obviously
   * takes one, and a thicket is the only place a whole group can vanish into
   * together. That spread is the point — it makes "which spot" a decision when
   * a team is running from something, rather than everyone diving at whatever
   * is nearest.
   */
  CAPACITY: {
    /** An upright steel locker. One person, standing very still. */
    locker: 1,
    /** A car boot. Room for exactly one, curled up. */
    trunk: 1,
    /** Four metres of hollow log — two, head to toe. */
    log: 2,
    /** A cluster of bushes, and the only spot that hides a whole group. */
    thicket: 3,
  },

  /** Seconds you can hold your breath before gasping. */
  BREATH_MAX: 9,
  /** Recovery speed as a multiple of the drain rate — recovering is faster. */
  BREATH_REGEN: 1.6,
  /** Monster proximity that forces you to want to hold your breath. */
  TENSION_RADIUS: 14,
  /** Chance per search that the monster leans in and checks your spot. */
  DISCOVERY_CHANCE: 0.22,
} as const;

// ---------------------------------------------------------------------------
// Extraction / win condition
// ---------------------------------------------------------------------------

export const EXTRACTION = {
  /** Seconds from rescue call to the helicopter arriving. */
  INBOUND_TIME: 150,
  /** Radius of the extraction zone. */
  ZONE_RADIUS: 12,
  /** Seconds you must survive inside the zone once the chopper lands. */
  HOLD_TIME: 20,
} as const;

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

/**
 * The view pulls back off your shoulder in the two places you are pinned.
 *
 * Hiding and climbing are the only states where you surrender movement, and
 * both are spent waiting and watching — for him to walk past the bush, or for
 * the ground below the ladder to be clear. First person is exactly wrong for
 * that: it shows you a handful of leaves or a rung, at the moment you most
 * need to read the forest. Pulling out also lets you finally see yourself,
 * which is what makes hiding feel like hiding rather than like a black screen.
 */
export const CAMERA = {
  /** How far behind you the view sits while tucked into a hiding place. */
  HIDING_DISTANCE: 2.8,
  /** Further on a ladder, where the height is worth seeing. */
  CLIMB_DISTANCE: 3.4,
  /**
   * How quickly the view travels between first and third person.
   *
   * Fast enough to read as a cut rather than a glide — you are ducking into
   * cover, not panning a cutscene — but not a single-frame teleport, which is
   * disorienting when it happens every time you press E.
   */
  TRANSITION_RATE: 12,
  /** The camera never drops closer than this to the ground behind you. */
  GROUND_CLEARANCE: 0.45,
  /**
   * How far back the view has to be before your own body and torch are drawn.
   *
   * Below this the camera is inside your own head: the torch casing becomes a
   * slab across the lens and the figure a wall of shoulder. One threshold for
   * both, because they are the same rig and appearing at different distances
   * would read as the torch detaching from the hand holding it.
   */
  SELF_DRAW_DISTANCE: 0.6,
} as const;

// ---------------------------------------------------------------------------
// Rendering / atmosphere (client-side, but kept here so it is tunable)
// ---------------------------------------------------------------------------

/**
 * Atmosphere and lighting.
 *
 * These are calibrated together and are highly interdependent — light
 * intensity, material albedo, fog density and tone-mapping exposure all
 * multiply. The first pass at these values was far too dark: dim lights times
 * dark bark times ACES tone mapping produced a literally black screen. If you
 * change one of these, look at the result before changing a second.
 *
 * The target is "moonlit night": silhouettes and ground are readable a few
 * metres out, detail needs the torch, and nothing is legible past the fog.
 */
export const ATMOSPHERE = {
  /**
   * Exponential-squared fog density. Fog fraction is 1 - exp(-(d*x)^2), so at
   * 0.022 you get roughly 50% fog at 35 m and 90% at 60 m — the sweet spot
   * where the forest closes in without blinding you.
   */
  FOG_DENSITY: 0.031,
  /** Nothing beyond this is drawn at all. */
  DRAW_DISTANCE: 140,
  /** Moonlight. Carries silhouettes; deliberately too weak to read detail by. */
  MOON_INTENSITY: 1.15,
  AMBIENT_INTENSITY: 0.3,
  /** Candela. Physical falloff means this needs to be far larger than it looks. */
  FLASHLIGHT_INTENSITY: 90,
  FLASHLIGHT_ANGLE: 0.42, // radians
  FLASHLIGHT_PENUMBRA: 0.55,
  FLASHLIGHT_RANGE: 45,
  FLASHLIGHT_DECAY: 1.1,
  TONE_EXPOSURE: 1.25,
} as const;
